/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

const useApplicationList = (fields: string) => {
  const { get } = useAbortController();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const queryKey = useMemo(() => ['application', 'list', loanNumber, token], [loanNumber, token]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!loanNumber || !token) return { data: [] };
      try {
        const request = {
          url: process.env.REACT_APP_APPLICATION_LIST.replace('?', loanNumber) + (fields ? '?fields=' + fields : ''),
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        return { data: response };
      } catch (error) {
        console.error(`useApplicationList.caught: ${error.message}`);
        return { data: [] };
      }
    },
  });

  const reloadApplicationList = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  return {
    loadingApplicationList: isLoading || isFetching,
    reloadApplicationList,
    applicationList: data?.data ? data.data : null,
  };
};

export default useApplicationList;
